import { useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Calculator } from 'lucide-react';

interface CustomServingInputProps {
  onServingsChange: (servings: number) => void;
  minServings?: number;
  maxServings?: number;
}

/**
 * Custom Serving Input Component
 * 
 * Allows chefs to input any custom serving size for events, catering, or special occasions.
 * Validates input and provides clear feedback for valid ranges.
 */
export function CustomServingInput({ 
  onServingsChange, 
  minServings = 1, 
  maxServings = 10000 
}: CustomServingInputProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const handleCalculate = () => {
    const num = parseInt(value, 10);
    
    if (isNaN(num)) {
      setError('Please enter a valid number');
      return;
    }
    
    if (num < minServings || num > maxServings) {
      setError(`Enter a number between ${minServings} and ${maxServings}`);
      return;
    }

    setError('');
    onServingsChange(num);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCalculate();
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-martinique">
        Custom Servings
      </label>
      <div className="flex gap-2">
        <Input
          type="number"
          placeholder="Enter number..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyPress={handleKeyPress}
          min={minServings}
          max={maxServings}
          className="flex-1"
          aria-label="Custom serving size"
        />
        <Button 
          onClick={handleCalculate} 
          variant="secondary"
          className="shrink-0"
        >
          <Calculator className="mr-2 h-4 w-4" />
          Calculate
        </Button>
      </div>
      {error && (
        <p className="text-sm text-red-600" role="alert">{error}</p>
      )}
      <p className="text-xs text-muted-foreground">
        For events, catering, or special occasions (1-10,000)
      </p>
    </div>
  );
}
